import React, { useState } from 'react';
import { 
  Typography, 
  Tabs, 
  Button, 
  Space, 
  Popconfirm, 
  message, 
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Switch
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserOutlined,
  BankOutlined,
  CarOutlined,
  TeamOutlined,
  HomeOutlined,
  QuestionOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { navigateToReferenceCard } from '../../utils/referenceCardNavigation';
import { DataTable, PageLayout } from '../../ui';
import useHasPermission from '../../hooks/useHasPermission';
import { useAuth } from '../../hooks/useAuth';

// API hooks
import {
  useGetAllContractorsQuery,
  useDeleteContractorMutation,
  useGetWarehousesQuery,
  useDeleteWarehouseMutation,
  useGetDriversQuery,
  useDeleteDriverMutation,
  useGetVehiclesQuery,
  useDeleteVehicleMutation,
  useGetCarriersQuery,
  useDeleteCarrierMutation,
  useGetRailwayStationsQuery,
  useDeleteRailwayStationMutation,
  useGetAirportsQuery,
  useDeleteAirportMutation,
  Contractor,
  Warehouse,
  Driver,
  Vehicle,
  Carrier,
  RailwayStation,
  Airport
} from '../../store/api/referencesApi';

// Модальные окна
import CreateContractorModal from '../../components/common/CreateContractorModal';
import CreateWarehouseModal from '../../components/common/CreateWarehouseModal';
import CreateDriverModal from '../../components/common/CreateDriverModal';
import CreateVehicleModal from '../../components/common/CreateVehicleModal';
import CreateCarrierModal from '../../components/common/CreateCarrierModal';
import CreateRailwayStationModal from '../../components/common/CreateRailwayStationModal';
import CreateAirportModal from '../../components/common/CreateAirportModal';
import { vehicleCategoryLabel } from '../../constants/vehicleCategory';

const { Text } = Typography;
const { TabPane } = Tabs;

// Типы для модальных окон
type ModalType = 'contractor' | 'warehouse' | 'driver' | 'vehicle' | 'carrier' | 'railwayStation' | 'airport';
type ModalMode = 'create' | 'edit';

interface ModalState {
  type: ModalType | null;
  mode: ModalMode;
  data: any;
}

const ReferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalState, setModalState] = useState<ModalState>({ type: null, mode: 'create', data: null });
  
  // Состояние для поиска по каждой вкладке
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({
    contractors: '',
    warehouses: '',
    drivers: '',
    vehicles: '',
    carriers: '',
    railwayStations: '',
    airports: ''
  });

  // Фильтр "только наши" для водителей и транспорта
  const [showOnlyCompanyDrivers, setShowOnlyCompanyDrivers] = useState(false);
  const [showOnlyCompanyVehicles, setShowOnlyCompanyVehicles] = useState(false);

  const canViewReferences = useHasPermission('references.view');
  const canEditReferences = useHasPermission('references.edit');
  const canCreateReferences = useHasPermission('references.create');
  const canModifyReferences = canEditReferences || canCreateReferences;
  const { user } = useAuth();

  // API queries (skip when user has no view permission)
  const { data: contractors = [], isLoading: contractorsLoading } = useGetAllContractorsQuery(undefined, { skip: !canViewReferences });
  const { data: warehouses = [], isLoading: warehousesLoading } = useGetWarehousesQuery(undefined, { skip: !canViewReferences });
  const { data: drivers = [], isLoading: driversLoading } = useGetDriversQuery(undefined, { skip: !canViewReferences });
  const { data: vehicles = [], isLoading: vehiclesLoading } = useGetVehiclesQuery(undefined, { skip: !canViewReferences });
  const { data: carriers = [], isLoading: carriersLoading } = useGetCarriersQuery(undefined, { skip: !canViewReferences });
  const { data: railwayStations = [], isLoading: railwayStationsLoading } = useGetRailwayStationsQuery(undefined, { skip: !canViewReferences });
  const { data: airports = [], isLoading: airportsLoading } = useGetAirportsQuery(undefined, { skip: !canViewReferences });

  // Delete mutations (defined unconditionally to satisfy hooks rules)
  const [deleteContractor] = useDeleteContractorMutation();
  const [deleteWarehouse] = useDeleteWarehouseMutation();
  const [deleteDriver] = useDeleteDriverMutation();
  const [deleteVehicle] = useDeleteVehicleMutation();
  const [deleteCarrier] = useDeleteCarrierMutation();
  const [deleteRailwayStation] = useDeleteRailwayStationMutation();
  const [deleteAirport] = useDeleteAirportMutation();

  // If user cannot view references, show friendly message and do not render tabs (prevents 403 errors)
  if (!canViewReferences) {
    return (
      <PageLayout title="Справочники">
        <Card>
          <Text>У вас нет прав для просмотра справочников.</Text>
        </Card>
      </PageLayout>
    );
  }

  

  // Обработчики модальных окон
  const openCreateModal = (type: ModalType) => {
    setModalState({ type, mode: 'create', data: null });
  };

  const openEditModal = (type: ModalType, data: any) => {
    setModalState({ type, mode: 'edit', data });
  };

  const closeModal = () => {
    setModalState({ type: null, mode: 'create', data: null });
  };

  const handleSuccess = () => {
    closeModal();
    message.success('Операция выполнена успешно');
  };

  // Функции фильтрации данных
  const filterData = (data: any[], query: string, searchFields: string[]) => {
    if (!query.trim()) return data;
    
    const normalizedQuery = query.toLowerCase().trim();
    return data.filter(item => 
      searchFields.some(field => {
        const value = item[field];
        if (!value) return false;
        
        // Для контрагентов нормализуем название (убираем префиксы)
        if (field === 'name' && typeof value === 'string') {
          const normalizedName = normalizeCompanyName(value);
          return normalizedName.includes(normalizedQuery);
        }
        
        return value.toString().toLowerCase().includes(normalizedQuery);
      })
    );
  };

  // Нормализация названия компании (убираем префиксы)
  const normalizeCompanyName = (name: string): string => {
    if (!name) return '';
    
    const prefixes = [
      'ооо', 'оао', 'ао', 'пао', 'ип', 'зао', 'оао', 'пao', 'тoo', 'нaо', 'кoo',
      'общество с ограниченной ответственностью',
      'открытое акционерное общество',
      'акционерное общество',
      'публичное акционерное общество',
      'индивидуальный предприниматель',
      'закрытое акционерное общество'
    ];
    
    let normalized = name.toLowerCase().trim();
    
    // Убираем префиксы
    for (const prefix of prefixes) {
      if (normalized.startsWith(prefix + ' ')) {
        normalized = normalized.substring(prefix.length + 1).trim();
        break;
      }
      if (normalized.startsWith(prefix + '«')) {
        normalized = normalized.substring(prefix.length + 1).trim();
        break;
      }
      if (normalized.startsWith(prefix + '"')) {
        normalized = normalized.substring(prefix.length + 1).trim();
        break;
      }
    }
    
    // Убираем кавычки
    normalized = normalized.replace(/[«»""]/g, '').trim();
    
    return normalized;
  };

  // Обработчик изменения поискового запроса
  const handleSearchChange = (tabKey: string, value: string) => {
    setSearchQueries(prev => ({
      ...prev,
      [tabKey]: value
    }));
  };

  // Отфильтрованные данные
  const filteredContractors = filterData(contractors, searchQueries.contractors, ['name', 'contactPerson', 'phone', 'inn']);
  const filteredWarehouses = filterData(warehouses, searchQueries.warehouses, ['name', 'city', 'address', 'contactPerson', 'phone']);
  
  // Водители: сначала фильтруем по "только наши", потом по поиску
  const driversAfterCompanyFilter = showOnlyCompanyDrivers 
    ? drivers.filter((d: Driver) => d.isCompanyDriver) 
    : drivers;
  const filteredDrivers = filterData(driversAfterCompanyFilter, searchQueries.drivers, ['fullName', 'phone', 'passportData', 'driverLicense']);
  
  // Транспорт: сначала фильтруем по "только наши", потом по поиску
  const vehiclesAfterCompanyFilter = showOnlyCompanyVehicles 
    ? vehicles.filter((v: Vehicle) => v.isCompanyVehicle) 
    : vehicles;
  const filteredVehicles = filterData(vehiclesAfterCompanyFilter, searchQueries.vehicles, [
    'registrationNumber',
    'vehicleType',
    'brand',
    'wheelFormula',
    'dimensionsText'
  ]);
  
  const filteredCarriers = filterData(carriers, searchQueries.carriers, ['name', 'contactPerson', 'phone', 'inn', 'kpp']);
  const filteredRailwayStations = filterData(railwayStations, searchQueries.railwayStations, ['name', 'code', 'city', 'region', 'country']);
  const filteredAirports = filterData(airports, searchQueries.airports, ['name', 'iataCode', 'icaoCode', 'city', 'region', 'country']);
  
  // Счётчики для статистики
  const companyDriversCount = drivers.filter((d: Driver) => d.isCompanyDriver).length;
  const companyVehiclesCount = vehicles.filter((v: Vehicle) => v.isCompanyVehicle).length;

  // Обработчики удаления
  const handleDelete = async (type: ModalType, id: number, name: string) => {
    try {
      switch (type) {
        case 'contractor':
          await deleteContractor(id).unwrap();
          break;
        case 'warehouse':
          await deleteWarehouse(id).unwrap();
          break;
        case 'driver':
          await deleteDriver(id).unwrap();
          break;
        case 'vehicle':
          await deleteVehicle(id).unwrap();
          break;
        case 'carrier':
          await deleteCarrier(id).unwrap();
          break;
        case 'railwayStation':
          await deleteRailwayStation(id).unwrap();
          break;
        case 'airport':
          await deleteAirport(id).unwrap();
          break;
      }
      message.success(`${name} успешно удален`);
    } catch (error: any) {
      message.error(error?.data?.error || 'Ошибка при удалении');
    }
  };

  // Конфигурации таблиц
  const contractorColumns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Contractor, b: Contractor) => a.name.localeCompare(b.name),
    },
    {
      title: 'Роли',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => (
        <Space>
          {roles.map(role => (
                       <Tag key={role} color="blue">
              {role === 'client' ? 'Клиент' : 
               role === 'expeditor' ? 'Экспедитор' :
               role === 'shipper' ? 'Отправитель' :
               role === 'consignee' ? 'Получатель' :
               role === 'carrier' ? 'Перевозчик (устар.)' : role}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Контактное лицо',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Contractor) => (
        <Space onClick={(e) => e.stopPropagation()}>
          {canModifyReferences && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openEditModal('contractor', record);
              }}
            >
              Редактировать
            </Button>
          )}
          {user?.role?.name === 'admin' && (
            <Popconfirm
              title="Удалить контрагента?"
              description="Это действие нельзя отменить."
              onConfirm={() => handleDelete('contractor', record.id, record.name)}
              okText="Да"
              cancelText="Нет"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              >
                Удалить
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const warehouseColumns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Warehouse, b: Warehouse) => a.name.localeCompare(b.name),
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city',
      sorter: (a: Warehouse, b: Warehouse) => a.city.localeCompare(b.city),
    },
    {
      title: 'Адрес',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Контактное лицо',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Warehouse) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => openEditModal('warehouse', record)}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить склад?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete('warehouse', record.id, record.name)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const driverColumns = [
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: (a: Driver, b: Driver) => a.fullName.localeCompare(b.fullName),
      render: (_: unknown, record: Driver) => (
        <Link
          to={`/drivers/${record.id}`}
          state={{ backgroundLocation: location }}
          onClick={(e) => e.stopPropagation()}
        >
          {record.fullName}
        </Link>
      )
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Паспортные данные',
      dataIndex: 'passportData',
      key: 'passportData',
    },
    {
      title: 'Водительские права',
      dataIndex: 'driverLicense',
      key: 'driverLicense',
    },
    {
      title: 'Водитель компании',
      key: 'isCompanyDriver',
      render: (_: any, record: Driver) => (
        <Tag color={record.isCompanyDriver ? 'green' : 'default'}>
          {record.isCompanyDriver ? 'Да' : 'Нет'}
        </Tag>
      ),
    },
    {
      title: 'Тягач / прицеп',
      key: 'defaultVehicles',
      render: (_: unknown, record: Driver) => (
        <Space direction="vertical" size={0}>
          {record.defaultVehicle ? (
            <span>
              <Text strong>{record.defaultVehicle.registrationNumber}</Text>
              <Text type="secondary"> · {vehicleCategoryLabel(record.defaultVehicle.vehicleCategory)}</Text>
              {record.defaultVehicle.brand ? (
                <Text type="secondary">
                  {' '}
                  ({record.defaultVehicle.brand})
                </Text>
              ) : null}
            </span>
          ) : (
            <Text type="secondary">Тягач не указан</Text>
          )}
          {record.defaultTrailerVehicle ? (
            <span>
              <Text strong>{record.defaultTrailerVehicle.registrationNumber}</Text>
              <Text type="secondary"> · {vehicleCategoryLabel(record.defaultTrailerVehicle.vehicleCategory)}</Text>
              {record.defaultTrailerVehicle.brand ? (
                <Text type="secondary">
                  {' '}
                  ({record.defaultTrailerVehicle.brand})
                </Text>
              ) : null}
            </span>
          ) : (
            <Text type="secondary">Прицеп не указан</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Driver) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal('driver', record)}
          >
            Редактировать
          </Button>
          <Button
            type="link"
            onClick={() => {
              const truckStr = record.defaultVehicle
                ? `тягач ${record.defaultVehicle.registrationNumber} (${vehicleCategoryLabel(record.defaultVehicle.vehicleCategory)})`
                : '';
              const trailerStr = record.defaultTrailerVehicle
                ? `прицеп ${record.defaultTrailerVehicle.registrationNumber} (${vehicleCategoryLabel(record.defaultTrailerVehicle.vehicleCategory)})`
                : '';
              const vehiclesPart = [truckStr, trailerStr].filter(Boolean).join('; ');
              const text = `Водитель ${record.fullName}, паспорт ${record.passportData || ''}, вод. уд. ${record.driverLicense || ''}${vehiclesPart ? `, ${vehiclesPart}` : ''}. Тел.: ${record.phone || ''}`.replace(/\s+/g, ' ').trim();
              const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Водитель ${record.fullName}.txt`;
              document.body.appendChild(a);
              a.click();
              URL.revokeObjectURL(url);
              a.remove();
            }}
          >
            Экспорт TXT
          </Button>
          <Popconfirm
            title="Удалить водителя?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete('driver', record.id, record.fullName)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const vehicleColumns = [
    {
      title: 'Регистрационный номер',
      dataIndex: 'registrationNumber',
      key: 'registrationNumber',
      sorter: (a: Vehicle, b: Vehicle) => a.registrationNumber.localeCompare(b.registrationNumber),
    },
    {
      title: 'Категория',
      key: 'vehicleCategory',
      width: 110,
      render: (_: unknown, record: Vehicle) => (
        <Tag>{vehicleCategoryLabel(record.vehicleCategory)}</Tag>
      ),
    },
    {
      title: 'Тип прицепа / колёсная формула',
      key: 'vehicleTypeDetails',
      ellipsis: true,
      render: (_: unknown, record: Vehicle) => {
        if (record.vehicleCategory === 'TRAILER') {
          return record.vehicleType ?? <Text type="secondary">—</Text>;
        }
        const parts = [record.wheelFormula, record.vehicleType].filter(Boolean);
        return parts.length ? parts.join(' · ') : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Марка',
      dataIndex: 'brand',
      key: 'brand',
    },
    {
      title: 'Грузоподъемность (кг)',
      dataIndex: 'carryingCapacity',
      key: 'carryingCapacity',
      render: (v: number | undefined) =>
        v != null && !Number.isNaN(v) ? v : <Text type="secondary">—</Text>,
    },
    {
      title: 'Объем (м³)',
      dataIndex: 'volumeM3',
      key: 'volumeM3',
      render: (v: number | undefined) =>
        v != null && !Number.isNaN(v) ? v : <Text type="secondary">—</Text>,
    },
    {
      title: 'Размеры',
      dataIndex: 'dimensionsText',
      key: 'dimensionsText',
      ellipsis: true,
      render: (v: string | undefined, record: Vehicle) =>
        record.vehicleCategory === 'TRAILER' && v ? v : <Text type="secondary">—</Text>,
    },
    {
      title: 'ТС компании',
      key: 'isCompanyVehicle',
      render: (_: any, record: Vehicle) => (
        <Tag color={record.isCompanyVehicle ? 'blue' : 'default'}>
          {record.isCompanyVehicle ? 'Да' : 'Нет'}
        </Tag>
      ),
    },
    {
      title: 'Закрепленный водитель',
      key: 'driversDefault',
      render: (_: any, record: Vehicle) => (
        record.driversDefault && record.driversDefault.length > 0 ? (
          <span>{record.driversDefault.map(d => d.fullName).join(', ')}</span>
        ) : (
          <Text type="secondary">—</Text>
        )
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Vehicle) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal('vehicle', record)}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить транспортное средство?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete('vehicle', record.id, record.registrationNumber)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const carrierColumns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Carrier, b: Carrier) => a.name.localeCompare(b.name),
    },
    {
      title: 'Юр. адрес',
      dataIndex: 'legalAddress',
      key: 'legalAddress',
      width: 260,
      ellipsis: true,
    },
    {
      title: 'Контактное лицо',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
    },
    {
      title: 'КПП',
      dataIndex: 'kpp',
      key: 'kpp',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Carrier) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openEditModal('carrier', record);
            }}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить перевозчика?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete('carrier', record.id, record.name)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const railwayStationColumns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: RailwayStation, b: RailwayStation) => a.name.localeCompare(b.name),
    },
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Регион',
      dataIndex: 'region',
      key: 'region',
    },
    {
      title: 'Страна',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: RailwayStation) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => openEditModal('railwayStation', record)}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить железнодорожную станцию?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete('railwayStation', record.id, record.name)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const airportColumns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Airport, b: Airport) => a.name.localeCompare(b.name),
    },
    {
      title: 'IATA код',
      dataIndex: 'iataCode',
      key: 'iataCode',
    },
    {
      title: 'ICAO код',
      dataIndex: 'icaoCode',
      key: 'icaoCode',
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Регион',
      dataIndex: 'region',
      key: 'region',
    },
    {
      title: 'Страна',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: Airport) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => openEditModal('airport', record)}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Удалить аэропорт?"
            description="Это действие нельзя отменить."
            onConfirm={() => handleDelete('airport', record.id, record.name)}
            okText="Да"
            cancelText="Нет"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout title="Справочники" subtitle="Управление справочными данными системы">
      {/* Статистика */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={3}>
          <Card>
            <Statistic 
              title="Контрагенты" 
              value={contractors.length} 
              prefix={<UserOutlined />} 
            />
          </Card>
        </Col>
        <Col span={3}>
          <Card>
            <Statistic 
              title="Склады" 
              value={warehouses.length} 
              prefix={<HomeOutlined />} 
            />
          </Card>
        </Col>
        <Col span={3}>
          <Card>
            <Statistic 
              title="Водители" 
              value={drivers.length} 
              prefix={<TeamOutlined />} 
            />
          </Card>
        </Col>
        <Col span={3}>
          <Card>
            <Statistic 
              title="Транспорт" 
              value={vehicles.length} 
              prefix={<CarOutlined />} 
            />
          </Card>
        </Col>
        <Col span={3}>
          <Card>
            <Statistic 
              title="Перевозчики" 
              value={carriers.length} 
              prefix={<BankOutlined />} 
            />
          </Card>
        </Col>
        <Col span={3}>
          <Card>
            <Statistic 
              title="ЖД станции" 
              value={railwayStations.length} 
              prefix={<QuestionOutlined />} 
            />
          </Card>
        </Col>
        <Col span={3}>
          <Card>
            <Statistic 
              title="Аэропорты" 
              value={airports.length} 
              prefix={<QuestionOutlined />} 
            />
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="contractors" type="card" style={{ backgroundColor: 'var(--card)', borderRadius: 8, padding: 8 }}>
        <TabPane 
          tab={
            <span>
              <UserOutlined />
              Контрагенты ({contractors.length})
            </span>
          } 
          key="contractors"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Input
              placeholder="Поиск по названию, контакту, телефону, ИНН..."
              prefix={<SearchOutlined />}
              value={searchQueries.contractors}
              onChange={(e) => handleSearchChange('contractors', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            {canModifyReferences && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => openCreateModal('contractor')}
              >
                Добавить контрагента
              </Button>
            )}
          </div>
          <DataTable
            columns={contractorColumns}
            dataSource={filteredContractors}
            loading={contractorsLoading}
            rowKey="id"
            rootClassName="references-contractors-table"
            onRow={(record) => ({
              onClick: () => navigateToReferenceCard(navigate, `/contractors/${record.id}`, location),
              style: { cursor: 'pointer' },
            })}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <HomeOutlined />
              Склады ({warehouses.length})
            </span>
          } 
          key="warehouses"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Input
              placeholder="Поиск по названию, городу, адресу, контакту..."
              prefix={<SearchOutlined />}
              value={searchQueries.warehouses}
              onChange={(e) => handleSearchChange('warehouses', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            {canModifyReferences && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => openCreateModal('warehouse')}
              >
                Добавить склад
              </Button>
            )}
          </div>
          <DataTable
            columns={warehouseColumns}
            dataSource={filteredWarehouses}
            loading={warehousesLoading}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <UserOutlined />
              Водители ({drivers.length})
            </span>
          } 
          key="drivers"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              placeholder="Поиск по ФИО, телефону, паспорту, правам..."
              prefix={<SearchOutlined />}
              value={searchQueries.drivers}
              onChange={(e) => handleSearchChange('drivers', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            <Space>
              <Switch 
                checked={showOnlyCompanyDrivers} 
                onChange={setShowOnlyCompanyDrivers}
                size="small"
              />
              <span style={{ color: showOnlyCompanyDrivers ? '#1890ff' : 'inherit' }}>
                Только наши ({companyDriversCount})
              </span>
            </Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('driver')}
            >
              Добавить водителя
            </Button>
          </div>
          <DataTable
            columns={driverColumns}
            dataSource={filteredDrivers}
            loading={driversLoading}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => navigateToReferenceCard(navigate, `/drivers/${record.id}`, location),
              style: { cursor: 'pointer' },
            })}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <CarOutlined />
              Транспорт ({vehicles.length})
            </span>
          } 
          key="vehicles"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Input
              placeholder="Поиск по номеру, типу, марке..."
              prefix={<SearchOutlined />}
              value={searchQueries.vehicles}
              onChange={(e) => handleSearchChange('vehicles', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            <Space>
              <Switch 
                checked={showOnlyCompanyVehicles} 
                onChange={setShowOnlyCompanyVehicles}
                size="small"
              />
              <span style={{ color: showOnlyCompanyVehicles ? '#1890ff' : 'inherit' }}>
                Только наши ({companyVehiclesCount})
              </span>
            </Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('vehicle')}
            >
              Добавить транспорт
            </Button>
          </div>
          <DataTable
            columns={vehicleColumns}
            dataSource={filteredVehicles}
            loading={vehiclesLoading}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => navigateToReferenceCard(navigate, `/vehicles/${record.id}`, location),
              style: { cursor: 'pointer' },
            })}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <TeamOutlined />
              Перевозчики ({carriers.length})
            </span>
          } 
          key="carriers"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Input
              placeholder="Поиск по названию, контакту, телефону, ИНН, КПП..."
              prefix={<SearchOutlined />}
              value={searchQueries.carriers}
              onChange={(e) => handleSearchChange('carriers', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('carrier')}
            >
              Добавить перевозчика
            </Button>
          </div>
          <DataTable
            columns={carrierColumns}
            dataSource={filteredCarriers}
            loading={carriersLoading}
            rowKey="id"
            rootClassName="references-carriers-table"
            onRow={(record) => ({
              onClick: () => navigateToReferenceCard(navigate, `/carriers/${record.id}`, location),
              style: { cursor: 'pointer' }
            })}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <CarOutlined />
              ЖД станции ({railwayStations.length})
            </span>
          } 
          key="railwayStations"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Input
              placeholder="Поиск по названию, коду, городу, региону, стране..."
              prefix={<SearchOutlined />}
              value={searchQueries.railwayStations}
              onChange={(e) => handleSearchChange('railwayStations', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('railwayStation')}
            >
              Добавить ЖД станцию
            </Button>
          </div>
          <DataTable
            columns={railwayStationColumns}
            dataSource={filteredRailwayStations}
            loading={railwayStationsLoading}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <CarOutlined />
              Аэропорты ({airports.length})
            </span>
          } 
          key="airports"
        >
          <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Input
              placeholder="Поиск по названию, IATA/ICAO коду, городу, региону, стране..."
              prefix={<SearchOutlined />}
              value={searchQueries.airports}
              onChange={(e) => handleSearchChange('airports', e.target.value)}
              style={{ maxWidth: '400px' }}
              allowClear
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('airport')}
            >
              Добавить аэропорт
            </Button>
          </div>
          <DataTable
            columns={airportColumns}
            dataSource={filteredAirports}
            loading={airportsLoading}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100','200'], showQuickJumper: true }}
          />
        </TabPane>
      </Tabs>

      {/* Модальные окна */}
      <CreateContractorModal
        isOpen={modalState.type === 'contractor'}
        onCancel={closeModal}
        onSuccess={handleSuccess}
        contractor={modalState.mode === 'edit' ? modalState.data : null}
        title={modalState.mode === 'edit' ? 'Редактировать контрагента' : 'Добавить контрагента'}
      />

      <CreateWarehouseModal
        isOpen={modalState.type === 'warehouse'}
        onCancel={closeModal}
        onSuccess={handleSuccess}
        warehouse={modalState.mode === 'edit' ? modalState.data : null}
        title={modalState.mode === 'edit' ? 'Редактировать склад' : 'Добавить склад'}
      />

      <CreateDriverModal
        isOpen={modalState.type === 'driver'}
        onClose={closeModal}
        onDriverCreated={handleSuccess}
        driver={modalState.mode === 'edit' ? modalState.data : null}
      />

      <CreateVehicleModal
        isOpen={modalState.type === 'vehicle'}
        onClose={closeModal}
        onVehicleCreated={handleSuccess}
        vehicle={modalState.mode === 'edit' ? modalState.data : null}
      />

      <CreateCarrierModal
        isOpen={modalState.type === 'carrier'}
        onClose={closeModal}
        onCarrierCreated={handleSuccess}
        carrier={modalState.mode === 'edit' ? modalState.data : null}
      />

      <CreateRailwayStationModal
        visible={modalState.type === 'railwayStation'}
        onCancel={closeModal}
        onSuccess={handleSuccess}
        station={modalState.mode === 'edit' ? modalState.data : null}
        title={modalState.mode === 'edit' ? 'Редактировать ЖД станцию' : 'Добавить ЖД станцию'}
      />

      <CreateAirportModal
        visible={modalState.type === 'airport'}
        onCancel={closeModal}
        onSuccess={handleSuccess}
        airport={modalState.mode === 'edit' ? modalState.data : null}
        title={modalState.mode === 'edit' ? 'Редактировать аэропорт' : 'Добавить аэропорт'}
      />
    </PageLayout>
  );
};

export default ReferencesPage; 